"use client";

import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { appDB, useLiveBlock } from "@/indexedDB";
import { useAuth } from "@/hooks/use-auth";
import { formatRelativeDate } from "@/lib/utils";

/** A relative string rots on screen. Re-render slowly so "just now" becomes
 *  "2m ago" without the user touching anything. */
const TICK_MS = 60_000;

/**
 * Header label: when this page was last edited, and by whom.
 *
 * The page block is the source of truth — server-written, and runEviction
 * explicitly keeps it while discarding everything else about a page. pageCache
 * is a freshness cache in front of it: the sync ack advances it every cycle,
 * which is what puts "just now" on screen while you type, but eviction deletes
 * the whole record once the block count passes its limit.
 *
 * Taking the newer of the two is safe in only one direction, and it's the right
 * one: both values are server-written, so neither can run AHEAD of reality. The
 * label can be stale, never wrong.
 *
 * Reads only — writing the page block from here would re-fire the seven per-key
 * liveQueries watching it once per sync cycle, which is the cost markBlockSynced
 * was made a no-op to remove.
 */
export default function LastEditedLabel({ noteId }: { noteId?: string }) {
  const { user } = useAuth();

  const entry = useLiveQuery(
    () => (noteId ? appDB.pageCache.get(noteId) : undefined),
    [noteId],
  );
  const block = useLiveBlock(noteId);

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const cachedAt = entry?.lastEditedAt;
  const rawBlockAt = block?.updatedAt ? new Date(block.updatedAt).getTime() : undefined;
  const blockAt = rawBlockAt != null && !Number.isNaN(rawBlockAt) ? rawBlockAt : undefined;

  const fromCache = cachedAt != null && (blockAt == null || cachedAt >= blockAt);
  const editedAt = fromCache ? cachedAt : blockAt;

  if (!noteId || editedAt == null) return null;

  const when = formatRelativeDate(editedAt);
  if (!when) return null;

  const author = fromCache ? entry?.lastEditedBy : block?.lastEditedBy;
  // With the block as source the server named the author at that same instant,
  // so there is no gap to interpret.
  const confirmedAt = fromCache ? entry?.lastEditedByAt : blockAt;

  // Only a local sync ack can push the time past the point the server last named
  // an author, so that gap means the newest edit was ours — the one case we can
  // attribute without asking the server.
  const selfEmail = (user?.email || "").toLowerCase();
  const isSelf =
    (confirmedAt != null && editedAt > confirmedAt) ||
    (!!selfEmail && author?.userEmail?.toLowerCase() === selfEmail);

  // "you" rather than your own name, which reads oddly when you are the one
  // looking at it. Everyone else is a first name — the header has no room for
  // more, and the tooltip carries the full one.
  const who = isSelf ? "you" : author?.userName?.trim().split(/\s+/)[0];
  const tooltipWho = isSelf ? "you" : author?.userName;

  return (
    <span
      // Hidden below `sm`: the header is 44px tall with the breadcrumb
      // competing for width, same call already made for the favourite star.
      className="hidden sm:inline max-w-[200px] shrink-0 truncate text-xs text-gray-500 dark:text-gray-400"
      title={tooltipWho ? `Last edited by ${tooltipWho}, ${when}` : `Last edited ${when}`}
    >
      {who ? `Edited by ${who} · ${when}` : `Edited ${when}`}
    </span>
  );
}
